import { GroupTreatmentFailureIcon } from 'components/Icons/GroupTreatmentFailureIcon'
import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useState } from 'react'
import {
    ButtonAction,
    dialogPanelDynamic
} from './styles'

interface FailedNotification {
    sys_id?: { value: string; display_value: string }
    number?: { value: string; display_value: string }
    incident_title?: { value: string; display_value: string }
    incident_description?: { value: string; display_value: string }
    sys_created_on?: { value: string; display_value: string }
}

interface GroupTreatmentFailureModalProps {
    isOpen: boolean
    handleCloseModal: () => void
    notificationStatus: string // '2' para 'Aguardando' ou '3' para 'Em Tratamento'
    failureType: 'total' | 'partial' // Novo parâmetro para diferenciar os cenários
    affectedCount?: number // Para falhas parciais
    failedNotifications?: FailedNotification[] // Notificações que falharam (para falha parcial)
}

const getMessageByStatus = (status: string, failureType: string, affectedCount?: number): string => {
    const baseMessage = failureType === 'total' 
        ? "Houve uma falha ao agrupar as notificações neste tratamento. O grupo foi desfeito, todas as notificações foram excluídos do grupo e"
        : `Houve uma falha ao agrupar as notificações neste tratamento. ${affectedCount} notificação(ões) foram excluídas do grupo e`
    
    switch (status) {
        case '2': // Aguardando
            return baseMessage + " voltaram para o status aguardando tratamento."
        case '3': // Em Tratamento
            return baseMessage + " continuam com o status em tratamento, porém de forma individual."
    }
}

const GroupTreatmentFailureModal: React.FC<GroupTreatmentFailureModalProps> = ({
    isOpen,
    handleCloseModal,
    notificationStatus,
    failureType,
    affectedCount,
    failedNotifications = []
}) => {
    const [isCopied, setIsCopied] = useState(false)
    const copyProtocolsToClipboard = () => {
        const protocols = failedNotifications
            .map(notification => notification.number?.display_value || notification.number?.value || 'N/A')
            .join('\n')
        
        navigator.clipboard.writeText(protocols).then(() => {
            setIsCopied(true)
            setTimeout(() => {
                setIsCopied(false)
            }, 2000)
            console.log('Protocolos copiados para a área de transferência')
        }).catch(err => {
            console.error('Erro ao copiar protocolos:', err)
        })
    }
    return (
        <>
            <Transition
                appear
                show={isOpen}
                as={Fragment}
                data-testid="test-GroupTreatmentFailure"
            >
                <Dialog
                    as="div"
                    className="relative z-10"
                    onClose={handleCloseModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className={dialogPanelDynamic(failureType)}>
                                    <div className="flex flex-col items-center" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
                                        <div style={{ width: '77px', height: '66px' }}>
                                            <GroupTreatmentFailureIcon />
                                        </div>
                                        
                                        <p className="text-xl font-semibold mt-2 mb-4 text-center" style={{ color: '#C43B51', marginLeft: '75px', marginRight: '75px' }}>
                                            Falha de agrupamento das notificações
                                        </p>
                                        
                                        <div className="text-base text-gray-700 mb-6 text-center" style={{ marginLeft: '104px', marginRight: '104px', whiteSpace: 'pre-line' }}>
                                            {getMessageByStatus(notificationStatus, failureType, affectedCount)}
                                        </div>

                                        {/* Seção de notificações que falharam (apenas para falha parcial) */}
                                        {failureType === 'partial' && failedNotifications.length > 0 && (
                                            <div className="w-full mb-6 px-8">
                                                <div className="mb-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h3 className="text-[14px] font-semibold text-blueTheme ml-4">Notificações excluídas do grupo</h3>
                                                        <button
                                                            onClick={copyProtocolsToClipboard}
                                                            className="flex items-center space-x-2 text-blueTheme hover:text-blue-700 transition-colors"
                                                        >
                                                            {isCopied ? (
                                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M16.3602 4.60378C16.5568 4.39816 16.5446 4.07669 16.3328 3.88576C16.121 3.69483 15.7899 3.70673 15.5933 3.91235L6.89437 13.0085L2.3946 8.60736C2.191 8.40823 1.8597 8.40705 1.65461 8.60474C1.44952 8.80243 1.44831 9.12411 1.65191 9.32325L6.53563 14.0999C6.636 14.198 6.77326 14.2522 6.91571 14.2499C7.05815 14.2476 7.19347 14.189 7.29041 14.0876L16.3602 4.60378Z" fill="#005EB8"/>
                                                                </svg>
                                                            ) : (
                                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M5.83934 1.65325C5.93747 1.55512 6.07057 1.5 6.20934 1.5L15.9767 1.50017C16.2657 1.50017 16.5 1.73444 16.5 2.02342L16.5 11.7907C16.5 11.9295 16.4449 12.0626 16.3467 12.1607C16.2486 12.2589 16.1155 12.314 15.9767 12.314H14.5814C14.2924 12.314 14.0581 12.0797 14.0581 11.7907C14.0581 11.5018 14.2924 11.2675 14.5814 11.2675H15.4535L15.4535 2.54666L6.73259 2.54651V3.41858C6.73259 3.70757 6.49832 3.94183 6.20933 3.94183C5.92035 3.94183 5.68608 3.70757 5.68608 3.41858V2.02325C5.68608 1.88447 5.74121 1.75138 5.83934 1.65325Z" fill="#005EB8"/>
                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M1.50015 6.20937C1.50018 5.92041 1.73444 5.68617 2.02341 5.68617H11.7908C12.0798 5.68617 12.314 5.92042 12.3141 6.20939L12.3141 15.9768C12.3141 16.2657 12.0798 16.5 11.7908 16.5H2.0233C1.73432 16.5 1.50005 16.2654 1.50005 15.9765C1.50005 15.9765 1.50005 15.9768 2.0233 15.9768L1.50005 15.9765L1.5 12.2165C1.5 10.1491 1.50002 7.70724 1.50015 6.20937ZM2.54662 6.73267L2.54655 15.4535H11.2676L11.2676 6.73267H2.54662Z" fill="#005EB8"/>
                                                                </svg>
                                                            )}
                                                            <span className="text-sm font-medium">{isCopied ? 'Copiado' : 'Copiar'}</span>
                                                        </button>
                                                    </div>
                                                    <div className="flex items-center justify-between mb-2 ml-4">
                                                        <span className="bg-[#D9D9D9] text-darkGray px-2 py-1 rounded-full text-[14px]">
                                                            {failedNotifications.length} notificação(ões)
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <div className="border border-gray-200 bg-white mx-auto" style={{ width: '468px' }}>
                                                    <div 
                                                        className="overflow-y-auto"
                                                        style={{ 
                                                            maxHeight: failedNotifications.length > 3 ? '144px' : 'auto'
                                                        }}
                                                    >
                                                        <table className="w-full">
                                                            <thead className="bg-[#EEEEF4] sticky top-0">
                                                                <tr>
                                                                    <th className="text-left py-2 px-3 text-[13px] font-semibold text-darkGray border-r border-gray-200">
                                                                        Título do incidente
                                                                    </th>
                                                                    <th className="text-left py-2 px-3 text-[13px] font-semibold text-darkGray">
                                                                        Chave
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {failedNotifications.map((notification, index) => (
                                                                    <tr key={notification.sys_id?.value || notification.number?.value || index} className="hover:bg-gray-50">
                                                                        <td className="py-2 px-3 text-xs text-textSelect max-w-xs border-r border-gray-200">
                                                                            {notification.incident_title?.display_value || notification.incident_title?.value || 'Sem título'}
                                                                        </td>
                                                                        <td className="py-2 px-3 text-xs text-textSelect">
                                                                            {notification.number?.display_value || notification.number?.value || 'Protocolo não encontrado'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <ButtonAction
                                            onClick={handleCloseModal}
                                            className="bg-blueTheme text-white text-sm"
                                            style={{ marginBottom: '32px' }}
                                        >
                                            Ok
                                        </ButtonAction>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

export { GroupTreatmentFailureModal }

